import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, Lock, Sparkle } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { toolsHubPath } from '@/lib/toolRoutes';
import Logo from '@/components/Logo';
import ToolTour, { ReplayTourButton } from '@/components/ToolTour';
import { buildSteps } from '@/lib/tourSteps';

export default function ToolLayout({ title, subtitle, children, testId, tourId }) {
  const { t, language } = useLanguage();
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const hubHref = toolsHubPath(language);
  const tourSteps = tourId ? buildSteps(tourId, t) : [];
  // Mark that the visitor used a free tool — feeds the dashboard checklist.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('growthriq_used_free_tool_v1', '1'); } catch (_) { /* noop */ }
    }
  }, []);

  // Auth gate — free tools require account (firma bilgisi + lead collection).
  // Only render the actual tool once we've resolved the auth state.
  const nextPath = encodeURIComponent(location.pathname + location.search);
  const showGate = !loading && !isAuthenticated;

  return (
    <div className="min-h-screen bg-[#0F1B36] text-white" data-testid={testId}>
      {tourId && tourSteps.length > 0 && !showGate && (
        <ToolTour tourId={tourId} steps={tourSteps} />
      )}
      <header className="border-b border-white/10 bg-[#0F1B36]/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="tool-logo">
            <Logo variant="dark" size="md" />
          </Link>
          <div className="flex items-center gap-3">
            {tourId && !showGate && <ReplayTourButton tourId={tourId} />}
            <Link to={hubHref} className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="back-to-tools">
              <ArrowLeft size={14} weight="bold" /> {t('tools.layout.back')}
            </Link>
          </div>
        </div>
      </header>

      {showGate ? (
        <div className="max-w-2xl mx-auto px-6 py-16" data-testid="tool-auth-gate">
          <div className="rounded-2xl border border-[#3CD9B8]/30 bg-gradient-to-br from-[#3CD9B8]/5 to-white/[0.02] p-8 sm:p-12 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3CD9B8]/15 mb-5">
              <Lock size={26} weight="bold" className="text-[#3CD9B8]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">{title}</h2>
            <p className="text-white/70 mb-2 leading-relaxed">
              Bu aracı ücretsiz kullanabilmek için önce <strong>hesap oluşturmanız</strong> gerekiyor.
            </p>
            <p className="text-white/50 text-sm mb-8">
              Kayıt olurken firma bilgilerinizi paylaştığınız için araç sonuçları da <strong>size özel</strong> hazırlanır ve dashboard&apos;unuza kaydedilir.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link
                to={`/register?next=${nextPath}`}
                data-testid="tool-gate-register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold hover:opacity-90 transition"
              >
                <Sparkle size={18} weight="fill" /> Ücretsiz Hesap Aç
              </Link>
              <Link
                to={`/login?next=${nextPath}`}
                data-testid="tool-gate-login"
                className="inline-flex items-center justify-center px-6 py-3 border border-white/20 text-white rounded-lg font-medium hover:bg-white/5 transition"
              >
                Giriş Yap
              </Link>
            </div>
            <p className="text-xs text-white/40 mt-6">
              Kayıt 30 saniye · Kredi kartı gerekmez · İlk kapsamlı olgunluk raporu ücretsiz
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-6xl mx-auto px-6 pt-10 pb-6">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{title}</h1>
            {subtitle && <p className="text-white/70 max-w-3xl">{subtitle}</p>}
          </div>
          <div className="max-w-6xl mx-auto px-6 pb-32 sm:pb-16">{children}</div>
        </>
      )}
    </div>
  );
}

export function Card({ children, className = '' }) {
  return <div className={`rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 ${className}`}>{children}</div>;
}

export function Field({ label, children, hint, required }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-white/80 mb-1.5">
        {label}{required && <span className="text-[#3CD9B8] ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#3CD9B8] transition ${props.className || ''}`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-[#3CD9B8] transition min-h-[100px] ${props.className || ''}`}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#3CD9B8] transition ${props.className || ''}`}
    >
      {children}
    </select>
  );
}

export function PrimaryButton({ children, ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 px-6 py-3 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed ${props.className || ''}`}
    >
      {children}
    </button>
  );
}

export function ScoreBadge({ score, size = 'md' }) {
  let color = 'text-red-400 bg-red-500/10 border-red-500/30';
  if (score >= 80) color = 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
  else if (score >= 60) color = 'text-[#3CD9B8] bg-[#3CD9B8]/10 border-[#3CD9B8]/30';
  else if (score >= 40) color = 'text-amber-400 bg-amber-500/10 border-amber-500/30';
  const sizeClass = size === 'lg' ? 'text-3xl px-5 py-2.5' : 'text-base px-3 py-1';
  return <span className={`inline-flex items-center font-bold rounded-full border ${color} ${sizeClass}`}>{score}</span>;
}

export function StatusDot({ status }) {
  const colors = { good: 'bg-emerald-400', warn: 'bg-amber-400', bad: 'bg-red-400' };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] || 'bg-white/30'}`} />;
}

export function UpsellBanner() {
  const { t } = useLanguage();
  return (
    <div className="mt-10 rounded-xl border border-[#3CD9B8]/30 bg-gradient-to-r from-[#3CD9B8]/10 to-transparent p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div>
        <h4 className="font-semibold mb-1">{t('tools.upsell.deeper')}</h4>
        <p className="text-sm text-white/70">{t('tools.upsell.deeper.body')}</p>
      </div>
      <Link
        to="/register"
        data-testid="upsell-banner-cta"
        className="px-5 py-2.5 bg-[#3CD9B8] text-[#0F1B36] rounded-lg font-semibold text-sm whitespace-nowrap"
      >
        {t('tools.upsell.deeper.cta')}
      </Link>
    </div>
  );
}
